import Link from 'next/link';
import React, { DetailedHTMLProps, HTMLAttributes } from 'react';
import Box, { HeaderTheme } from 'src/components/ui-kit/box/box';
import Button, { ButtonSize } from 'src/components/ui-kit/button/button';
import ListingsDetailsItem from './item/item';
import styles from './details.module.scss';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { AuthSelectors } from 'src/store/auth';
import { Listing } from 'src/entities/listing';
import { ListingStatus } from 'src/enums/listing';
import Papper from 'src/components/ui-kit/papper/papper';
import { ProfileSelectors } from 'src/store/profile';

export default function ListingDetails({
  listing,
  className,
  ...restProps
}: {
  listing: Listing;
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>): JSX.Element {
  const { t } = useTranslation('components:pages:listings:view:details');
  const { t: tCommon } = useTranslation('common');

  const isAuthenticated = useSelector(AuthSelectors.isAuthenticated);
  const profile = useSelector(ProfileSelectors.profile);

  const guestFields = [
    { title: t('TEXT_NET_ACRES'), content: listing.netAcres },
    {
      title: t('TEXT_AVERAGE_MONTHLY_NET_CASH_FLOW'),
      content: listing.averageMonthlyCashFlow ? listing.formatUsAverageMonthlyCashFlow : ''
    }
  ];

  const userFields = [
    { title: t('TEXT_LEASE_BONUS'), content: listing.leaseBonus },
    { title: t('TEXT_OPERATOR'), content: listing.operator },
    { title: t('TEXT_ACTIVE_LEASE'), content: listing.isActiveLease ? tCommon('TEXT_YES') : tCommon('TEXT_NO') },
    { title: t('TEXT_LEASE_TEAMS'), content: listing.leaseTerms },
    { title: t('TEXT_ROYALTY_PERCENTAGE'), content: listing.royaltyPercentage ? `${listing.royaltyPercentage}%` : '' },
    {
      title: t('TEXT_HELD_BY_PRODUCTION'),
      content: listing.isHeldByProduction ? tCommon('TEXT_YES') : tCommon('TEXT_NO')
    },
    { title: t('TEXT_COMMENTS'), content: listing.comment, isLengthyDetails: true },
    { title: t('TEXT_BID_BASIS'), content: listing.bidBasis, isLengthyDetails: true }
  ];

  const listingDetailsGuest = guestFields.filter((item) => item.content);
  const listingDetailsUser = userFields.filter((item) => item.content);

  const isNotEmptyDetails =
    (listingDetailsGuest.length && !isAuthenticated) || (listingDetailsUser.length && isAuthenticated);

  return (
    <Box
      className={className}
      heading={t('TEXT_LISTING_DETAILS')}
      headingTheme={HeaderTheme.DARK}
      {...restProps}>
      <div className={styles.details}>
        {isAuthenticated && profile?.isActive && (
          <div className={styles.details_actions}>
            <Link href={`/buyer-inquiry-form/listing${listing?.id}`}>
              <Button
                size={ButtonSize.LARGE}
                disabled={listing?.status === ListingStatus.INACTIVE}
                className={styles.details_button}
              >
                {t('BUTTON_MAKE_OFFER')}
              </Button>
            </Link>
            <Link href={`/buyer-inquiry-form/listing${listing?.id}`}>
              <Button
                size={ButtonSize.LARGE}
                className={styles.details_button}>
                {t('BUTTON_INQUIRY_FORM')}
              </Button>
            </Link>
          </div>
        )}
        <Papper>
          {listing?.id && <ListingsDetailsItem
            title={t('TEXT_LISTING_ID')}
            content={listing.id} />}
          {isAuthenticated && listing?.startingBid && (
            <ListingsDetailsItem
              title={t('TEXT_STARTING_BID')}
              content={listing.startingBid} />
          )}
        </Papper>
        <h4 className={styles.details_subtitle}>{t('TEXT_PROPERTY_LOCATION')}</h4>
        <Papper>
          {listing?.state?.name && <ListingsDetailsItem
            title={t('TEXT_STATE')}
            content={listing.state.name} />}
          {listing?.county?.name && <ListingsDetailsItem
            title={t('TEXT_COUNTY')}
            content={listing.county.name} />}
          {isAuthenticated && profile?.isActive && listing?.legalLocation && (
            <ListingsDetailsItem
              title={t('TEXT_LEGAL_LOCATION')}
              content={listing.legalLocation} />
          )}
        </Papper>
        {isNotEmptyDetails && (
          <>
            <h4 className={styles.details_subtitle}>{t('TEXT_PROPERTY_DETAILS')}</h4>
            <Papper>
              {listingDetailsGuest &&
                listingDetailsGuest.map((listing) => (
                  <ListingsDetailsItem
                    key={listing.title}
                    title={listing.title}
                    content={listing.content} />
                ))}
              {isAuthenticated &&
                profile?.isActive &&
                listingDetailsUser &&
                listingDetailsUser.map((listing) => (
                  <ListingsDetailsItem
                    key={listing.title}
                    title={listing.title}
                    content={listing.content}
                    isLengthyDetails={listing.isLengthyDetails}
                  />
                ))}
            </Papper>
          </>
        )}
      </div>
    </Box>
  );
}
